import { useEffect, useRef } from 'react';
import { Chart, type ChartConfiguration, type Plugin } from 'chart.js/auto';
import { formatCurrency } from '../types';

export interface DonutSegment {
  label: string;
  amount: number;
  color: string;
}

/** Matches legacy's own `DashboardScript.js` construction exactly: a Chart.js doughnut with a 70% cutout
 * and a hand-drawn center-text plugin (legacy has no datalabels plugin either — this is the same
 * `afterDraw` approach, just in React).
 *
 * `centerSubtitle` is optional and defaults to `undefined` — omitting it renders the exact same hardcoded
 * "100.0%" every existing caller (the standalone `/dashboard`) already gets, where `total` genuinely is
 * the complete, whole-composition figure. Only a caller whose `total` is a subtotal of fewer categories
 * than the composition names (EPN-42's "Payroll Deductions Breakdown", where "Loans Repayment" is real but
 * unavailable and therefore excluded from `total`) should pass an honest replacement — "100.0%" there would
 * visually claim the chart is a complete breakdown when it isn't. */
export function DonutChart({ segments, total, centerSubtitle }: { segments: DonutSegment[]; total: number; centerSubtitle?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const nonZero = segments.filter((s) => s.amount > 0);
    const data = nonZero.length > 0 ? nonZero : [{ label: 'No data', amount: 1, color: '#e7e9ee' }];
    const subtitle = centerSubtitle ?? '100.0%';

    const centerTextPlugin: Plugin<'doughnut'> = {
      id: 'ed-center-text',
      afterDraw(chart) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        // The centre text follows the dashboard's own colour tokens, so it stays readable in both light and dark themes.
        const styles = getComputedStyle(canvas);
        const textColor = styles.getPropertyValue('--ed-text').trim() || '#1c2333';
        const mutedColor = styles.getPropertyValue('--ed-text-muted').trim() || '#6b7280';

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = textColor;
        ctx.font = "700 15px Poppins, sans-serif";
        ctx.fillText(formatCurrency(total), centerX, centerY - 8);

        ctx.fillStyle = mutedColor;
        ctx.font = "400 11px Poppins, sans-serif";
        ctx.fillText(subtitle, centerX, centerY + 10);
        ctx.restore();
      },
    };

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        labels: data.map((d) => d.label),
        datasets: [{ data: data.map((d) => d.amount), backgroundColor: data.map((d) => d.color), borderWidth: 0 }],
      },
      options: {
        cutout: '70%',
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: nonZero.length > 0 } },
      },
      plugins: [centerTextPlugin],
    };

    chartRef.current = new Chart(canvas, config);
    // The chart is drawn on a canvas, which CSS cannot restyle: redraw when the app switches between light and dark.
    const themeObserver = new MutationObserver(() => chartRef.current?.update());
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      themeObserver.disconnect();
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [segments, total, centerSubtitle]);

  return (
    <div className="ed-donut-wrap" role="img" aria-label={`Donut chart: ${formatCurrency(total)} total, broken down by ${segments.map((s) => s.label).join(', ')}`}>
      <canvas ref={canvasRef} />
    </div>
  );
}
